import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
  Paper,
  Card,
  CardContent,
  alpha,
  useTheme
} from '@mui/material';
import {
  Computer,
  Smartphone,
  Tablet,
  DevicesOther,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  DevicesOutlined,
  RefreshOutlined
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { 
  fetchUserDevices, 
  removeDevice, 
  renameDevice 
} from '../../../../redux/actions/deviceActions';
import { formatDistanceToNow } from 'date-fns';
import CustomButton from '../../../../components/CustomButton';

const DeviceManagement = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { devices, loading, error } = useSelector(state => state.devices);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    // Fetch user devices when component mounts or refresh is triggered
    dispatch(fetchUserDevices());
  }, [dispatch, refreshTrigger]);

  const handleDeleteClick = (device) => {
    setSelectedDevice(device);
    setOpenDeleteDialog(true);
  };

  const handleRenameClick = (device) => {
    setSelectedDevice(device);
    setNewDeviceName(device.name);
    setOpenRenameDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedDevice) {
      dispatch(removeDevice(selectedDevice.id));
    }
    setOpenDeleteDialog(false);
    setSelectedDevice(null);
  };

  const handleRenameConfirm = () => {
    if (selectedDevice && newDeviceName.trim()) {
      dispatch(renameDevice({
        deviceId: selectedDevice.id,
        newName: newDeviceName.trim()
      }));
    }
    setOpenRenameDialog(false);
    setSelectedDevice(null);
    setNewDeviceName('');
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'desktop':
        return <Computer />;
      case 'mobile':
        return <Smartphone />;
      case 'tablet':
        return <Tablet />;
      default:
        return <DevicesOther />;
    }
  };

  const formatLastActive = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (e) {
      return t('unknown_date');
    }
  };

  // Section header component (matching other tabs)
  const SectionHeader = ({ icon, title, description }) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {icon && (
          <Box
            sx={{
              mr: 2,
              p: 1.5,
              borderRadius: '12px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: { xs: '1.25rem', sm: '1.5rem' }
          }}
        >
          {title}
        </Typography>
      </Box>
      {description && (
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            ml: icon ? 7 : 0,
            mb: 2
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );

  // Empty state component
  const EmptyState = () => (
    <Box
      sx={{
        textAlign: 'center',
        py: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <Box
        sx={{
          p: 2,
          borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          color: theme.palette.primary.main,
          mb: 2
        }}
      >
        <DevicesOther sx={{ fontSize: 48 }} />
      </Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
        {t('no_devices')}
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ maxWidth: 400, mb: 3 }}>
        {t('no_devices_description', 'No devices found. When you log in on a new device, it will appear here.')}
      </Typography>
      <CustomButton
        text={t('refresh')}
        variant="outlined"
        startIcon={<RefreshOutlined />}
        onClick={handleRefresh}
      />
    </Box>
  );

  return (
    <Box sx={{ py: 2 }}>
      {/* Header section */}
      <SectionHeader 
        icon={<DevicesOutlined />}
        title={t('device_management')}
        description={t('device_management_description', 'Manage your connected devices and log out from devices you no longer use.')}
      />
      
      {/* Device Limit Info Banner */}
      <Card
        elevation={0}
        sx={{
          mb: 4,
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.default,
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                {t('device_limit_title', 'Device Limit')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('device_limit_description', 'You can connect up to 4 devices simultaneously. If you reach the limit, your oldest device will be logged out automatically.')}
              </Typography>
            </Box>
            <Box sx={{ mt: { xs: 2, sm: 0 } }}>
              <CustomButton 
                text={t('refresh')} 
                variant="outlined"
                startIcon={<RefreshOutlined />}
                onClick={handleRefresh}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      {/* Main content */}
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress sx={{ color: theme.palette.primary.main }} />
            </Box>
          ) : devices && devices.length > 0 ? (
            <List sx={{ p: 0 }}>
              {devices.map((device) => (
                <ListItem
                  key={device.id}
                  sx={{
                    mb: 2,
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: device.is_current ? alpha(theme.palette.primary.main, 0.3) : theme.palette.divider,
                    backgroundColor: device.is_current ? alpha(theme.palette.primary.main, 0.05) : theme.palette.background.paper,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: device.is_current ? theme.palette.primary.main : theme.palette.divider,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.text.primary, 0.05)}`
                    },
                    p: 2
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: '44px',
                      color: device.is_current ? theme.palette.primary.main : theme.palette.text.secondary
                    }}
                  >
                    {getDeviceIcon(device.device_type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <Typography variant="body1" fontWeight="medium" color={theme.palette.text.primary}>
                          {device.name}
                        </Typography>
                        {device.is_current && (
                          <Tooltip title={t('current_device')}>
                            <CheckCircleIcon
                              fontSize="small"
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color={theme.palette.text.secondary}>
                        {t('active_since', { time: formatLastActive(device.last_active) })}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={t('rename')}>
                      <IconButton
                        edge="end"
                        aria-label="rename"
                        onClick={() => handleRenameClick(device)}
                        disabled={loading}
                        sx={{
                          color: theme.palette.text.secondary,
                          '&:hover': { color: theme.palette.primary.main }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!device.is_current && (
                      <Tooltip title={t('disconnect')}>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDeleteClick(device)}
                          disabled={loading}
                          sx={{
                            ml: 1,
                            color: theme.palette.text.secondary,
                            '&:hover': { color: theme.palette.error.main }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <EmptyState />
          )}
        </CardContent>
      </Card>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxWidth: '400px'
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ pb: 1, pt: 3 }}>
          {t('disconnect_device')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ color: theme.palette.text.secondary }}>
            {t('disconnect_device_confirm', { name: selectedDevice?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.divider, 0.5)
              }
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              bgcolor: theme.palette.error.main,
              '&:hover': {
                bgcolor: alpha(theme.palette.error.main, 0.8)
              }
            }}
            autoFocus
          >
            {t('disconnect')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Rename Dialog */}
      <Dialog 
        open={openRenameDialog} 
        onClose={() => setOpenRenameDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '12px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3 }}>{t('rename_device')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('new_device_name')}
            type="text"
            fullWidth
            value={newDeviceName}
            onChange={(e) => setNewDeviceName(e.target.value)}
            error={!newDeviceName.trim()}
            helperText={!newDeviceName.trim() ? t('device_name_required') : ""}
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setOpenRenameDialog(false)}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.divider, 0.5)
              }
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleRenameConfirm}
            variant="contained"
            color="primary"
            disabled={!newDeviceName.trim()}
            sx={{
              bgcolor: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.dark
              }
            }}
          >
            {t('rename')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceManagement;